import { useStableImperativeCallback } from "./useStableImperativeCallback";
import { Coordinate } from "client/Misc/commonTypes";
import { never } from "client_legacy/ndaw/errors";
import {
	RefCallback,
	RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useForceRerender } from "./useForceRerender";
import { newGuid } from "client_legacy/ndaw/guid";

// Yes this is a global, we expect only a single draggable at a time, and this is the
// easiest solution to support nested draggables (i.e. the first "element" to start wins to become a draggable).
let isDragging: boolean = false;

export type DragCallbackArgs = {
	movement: Coordinate;
	clientX: number;
	clientY: number;
	pointerClientX: number;
	pointerClientY: number;
};

const draggableExcludeCallbackFactory = Symbol();

export type DraggableExcludeController = {
	[draggableExcludeCallbackFactory]: (identifier: string) => RefCallback<HTMLElement>;
};

export type UseDraggableResult = {
	ref: RefCallback<HTMLElement>;
	/**
	 * You can pass this controller to the {@link useExcludeFromDraggingRef} to be able to exclude
	 * children from being draggable. It could be useful for text inputs, scrollable containers
	 * and other elements whose behavior can interfere with draggable.
	 */
	excludeController: DraggableExcludeController;
};

export type UseDraggableOptions = {
	onStart: (args: DragCallbackArgs) => void;
	onMove: (args: DragCallbackArgs) => void;
	onStop: (args: DragCallbackArgs) => void;
	isEnabled?: boolean;
};

// This hook does not work on touch devices when the CSS `scroll-behavior` property is set to 'smooth'
// To ensure proper functionality, make sure that the scroll container does not have `scroll-behavior: smooth`

export function useDraggable(options: UseDraggableOptions): UseDraggableResult {
	const {
		onStart,
		onMove,
		onStop,
		isEnabled = true
	} = options;

	const [element, setElement] = useState<HTMLElement | null>(null);
	const [state, setState] = useState<DraggableState | null>(null);
	const dragStatusRef: RefObject<DragStatus | null> = useRef<DragStatus>(null);
	const elementsToExcludeIdMapRef: RefObject<Map<string, HTMLElement>> = useRef<Map<string, HTMLElement>>(null!);
	const edgeScrollingStateRef: RefObject<EdgeScrollingState> = useRef<EdgeScrollingState>(defaultEdgeScrollingState);
	const shouldStopPropagationOfNextClickRef: RefObject<boolean> = useRef<boolean>(false);
	const [rerender] = useForceRerender();

	if (!elementsToExcludeIdMapRef.current) {
		elementsToExcludeIdMapRef.current = new Map();
	}

	const stableOnStart: typeof onStart = useStableImperativeCallback(onStart);
	const stableOnMove: typeof onMove = useStableImperativeCallback(onMove);
	const stableOnStop: typeof onStop = useStableImperativeCallback(onStop);

	useEffect(
		(): EffectWithDestructorResult => {
			return (): void => {
				if (elementsToExcludeIdMapRef.current.size > 0) {
					console.error("Memory leak detected. The map of elements which should be excluded from the draggable functionality is not empty by the time component is unmounted.");
				}
			};
		},
		[]
	);

	useLayoutEffect(
		(): EffectWithDestructorResult => {
			if (!element || !isEnabled) {
				return;
			}

			const elementStyles: CSSStyleDeclaration = element.style;

			const originalValue: string = elementStyles.touchAction;

			// NOTE: touchAction controls which of the default browser touch gestures should work with the element.
			// It is set to the pinch-zoom, because it is the only multi-finger gesture, so it should not
			// conflict with our drag logic.
			// The style should be applied before the touchstart event is triggered in order for it to work.
			elementStyles.touchAction = "pinch-zoom";

			return (): void => {
				elementStyles.touchAction = originalValue;
			};
		},
		[element, isEnabled]
	);

	const updateEdgeScrollingState = useCallback(
		(
			args: MouseEvent | Touch,
			scrollContainer: DragScrollContainer
		): void => {
			const { pageX, pageY } = args;

			const { element } = scrollContainer;

			let scrollArea: {
				top: number;
				left: number;
				bottom: number;
				right: number;
			};

			if (element !== document.scrollingElement) {
				scrollArea = element.getBoundingClientRect();
			} else {
				scrollArea = {
					top: element.scrollTop,
					left: element.scrollLeft,
					bottom: element.scrollTop + element.clientHeight,
					right: element.scrollLeft + element.clientWidth
				};
			}

			let verticalSpeed: number;
			if ((pageY - scrollArea.top) < SCROLL_SENSITIVENESS) {
				verticalSpeed = -SCROLL_SPEED;
			} else if (scrollArea.bottom - pageY < SCROLL_SENSITIVENESS) {
				verticalSpeed = SCROLL_SPEED;
			} else {
				verticalSpeed = 0;
			}

			let horizontalSpeed: number;
			if ((pageX - scrollArea.left) < SCROLL_SENSITIVENESS) {
				horizontalSpeed = -SCROLL_SPEED;
			} else if ((scrollArea.right - pageX) < SCROLL_SENSITIVENESS) {
				horizontalSpeed = SCROLL_SPEED;
			} else {
				horizontalSpeed = 0;
			}

			const startArgs: MouseEvent | Touch | undefined = horizontalSpeed !== 0
				|| verticalSpeed !== 0
					? args
					: undefined;

			edgeScrollingStateRef.current = {
				horizontalSpeed,
				verticalSpeed,
				startArgs
			};

			rerender();
		},
		[rerender]
	);

	const hasEdgeScrolling: boolean = dragStatusRef.current === "active"
		&& (edgeScrollingStateRef.current.horizontalSpeed !== 0
			|| edgeScrollingStateRef.current.verticalSpeed !== 0
		);

	useEffect(
		(): EffectWithDestructorResult => {
			if (!hasEdgeScrolling
				|| !state
				|| !state.scrollContainer
				|| !isEnabled
			) {
				return;
			}

			const element: HTMLElement = state.scrollContainer
				.element;

			const intervalHandle: SetIntervalHandle = setInterval(
				(): void => {
					const {
						horizontalSpeed,
						verticalSpeed,
						startArgs
					} = edgeScrollingStateRef.current;

					element.scrollLeft += horizontalSpeed;
					element.scrollTop += verticalSpeed;

					if (startArgs) {
						const callbackArgs: DragCallbackArgs = getDragArguments(
							startArgs,
							state
						);

						stableOnMove(callbackArgs);
					}
				},
				25
			);

			return () => clearInterval(intervalHandle);
		},
		[
			hasEdgeScrolling,
			isEnabled,
			stableOnMove,
			state
		]
	);

	const pointerDownHandler = useCallback(
		(currentTarget: EventTarget | null, pointerArgs: MouseEvent | Touch): void => {
			if (isDragging) {
				return;
			}

			isDragging = true;

			if (!(currentTarget instanceof HTMLElement)) {
				console.error("Expected currentTarget to be an HTMLElement");
				return;
			}

			const {
				top,
				left
			} = currentTarget.getBoundingClientRect();

			const pointer: DragPointer = {
				...("identifier" in pointerArgs
					? {
						kind: "touch",
						id: pointerArgs.identifier
					} : {
						kind: "mouse"
					}
				),
				pageStartOffset: {
					x: pointerArgs.pageX,
					y: pointerArgs.pageY
				},
				offsetInElement: {
					x: pointerArgs.clientX - left,
					y: pointerArgs.clientY - top
				}
			};

			const scrollContainerElement: HTMLElement | null = findNearestScrollContainer(currentTarget);

			const scrollContainer: DragScrollContainer | null = scrollContainerElement
				? {
					element: scrollContainerElement,
					startOffset: {
						x: scrollContainerElement.scrollLeft,
						y: scrollContainerElement.scrollTop
					}
				}
				: null;

			// you need to move a bit before we consider it to be dragging
			dragStatusRef.current = "pending";
			// resetting the state to be in sync with the dragStatusRef
			shouldStopPropagationOfNextClickRef.current = false;

			setState(
				(): DraggableState => ({
					pointer,
					scrollContainer
				})
			);
		},
		[]
	);

	const shouldEventTargetBeDraggable = useCallback(
		(event: Event): boolean => {
			const elementsToExcludeIdMap: Map<string, HTMLElement> = elementsToExcludeIdMapRef
				.current;
			if (elementsToExcludeIdMap.size === 0) {
				return true;
			}

			const elementsToExclude = new Set<HTMLElement>(
				elementsToExcludeIdMap.values()
			);

			const isExcluded: boolean = event.composedPath()
				.some(p => elementsToExclude.has(p as HTMLElement));

			return !isExcluded;
		},
		[]
	);

	const touchStartHandler = useCallback(
		(e: TouchEvent): void => {
			if (e.touches.length !== 1) {
				return;
			};


			if (!shouldEventTargetBeDraggable(e)) {
				return;
			}

			pointerDownHandler(e.currentTarget, e.touches[0]);
		},
		[pointerDownHandler, shouldEventTargetBeDraggable]
	);

	const mouseDownHandler = useCallback(
		(e: MouseEvent): void => {
			if (!shouldEventTargetBeDraggable(e)) {
				return;
			}

			if (e.button !== 0) {
				return;
			}

			document.getSelection()
				?.removeAllRanges();

			pointerDownHandler(e.currentTarget, e);
		},
		[pointerDownHandler, shouldEventTargetBeDraggable]
	);

	const dragStartHandler = useCallback(
		(e: DragEvent): void => {
			if (!shouldEventTargetBeDraggable(e)) {
				return;
			}

			// This prevents native draggable elements such as anchors or images to interfere width our draggable logic
			e.preventDefault();
		},
		[shouldEventTargetBeDraggable]
	);

	useEffect(
		(): (() => void) | void => {
			if (!element || !isEnabled) {
				return;
			}

			const cancellation = new AbortController();
			const { signal } = cancellation;

			element.addEventListener(
				"mousedown",
				mouseDownHandler,
				{ signal }
			);

			element.addEventListener(
				"touchstart",
				touchStartHandler,
				{ signal }
			);

			element.addEventListener(
				"dragstart",
				dragStartHandler,
				{ signal }
			);

			// NOTE: This disables click handlers on children while dragging.
			// Click handler must be setup before mousedown event is triggered.
			// It cannot use dragStatusRef to determine if propagation should be stopped or not,
			// because by the time click handler is triggered dragStatusRef is already set to null from
			// the mouseup handler.
			element.addEventListener(
				"click",
				(event) => {
					if (shouldStopPropagationOfNextClickRef.current) {
						shouldStopPropagationOfNextClickRef.current = false;
						event.stopPropagation();
					}
				},
				{
					capture: true,
					signal
				}
			);

			return (): void => {
				cancellation.abort();
			};
		},
		[
			element,
			isEnabled,
			mouseDownHandler,
			touchStartHandler,
			dragStartHandler
		]
	);

	const pointerMoveHandler = useCallback(
		(args: MouseEvent | Touch, state: DraggableState): void => {
			const callbackArgs: DragCallbackArgs = getDragArguments(
				args,
				state
			);

			if (dragStatusRef.current === "pending") {
				if (hasMovedEnough(callbackArgs.movement)) {
					dragStatusRef.current = "active";
					shouldStopPropagationOfNextClickRef.current = true;

					const focusedElement: Element | null = document.activeElement;

					if (focusedElement instanceof HTMLElement) {
						focusedElement.blur();
					}

					stableOnStart(callbackArgs);
				}
			} else if (dragStatusRef.current === "active") {
				if (state.scrollContainer) {
					updateEdgeScrollingState(args, state.scrollContainer);
				}

				stableOnMove(callbackArgs);
			}
		},
		[
			updateEdgeScrollingState,
			stableOnMove,
			stableOnStart
		]
	);

	const pointerUpHandler = useCallback(
		(args: MouseEvent | Touch, state: DraggableState): void => {
			if (dragStatusRef.current === "active") {
				const callbackArgs: DragCallbackArgs = getDragArguments(
					args,
					state
				);

				stableOnStop(callbackArgs);
			}

			isDragging = false;
			dragStatusRef.current = null;
			edgeScrollingStateRef.current = defaultEdgeScrollingState;
			setState(null);
		},
		[stableOnStop]
	);

	useEffect(
		(): (() => void) | void => {
			if (!state || !isEnabled) {
				return;
			}

			const { pointer } = state;

			const cancellation = new AbortController();
			const { signal } = cancellation;

			switch (pointer.kind) {
				case "mouse":
					window.addEventListener(
						"mousemove",
						(args) => pointerMoveHandler(args, state),
						{ signal }
					);

					window.addEventListener(
						"mouseup",
						(args) => pointerUpHandler(args, state),
						{ signal }
					);

					document.addEventListener(
						"selectstart",
						(e) => e.preventDefault(),
						{ signal }
					);
					break;
				case "touch":
					window.addEventListener(
						"touchmove",
						(args) => {
							const touch: Touch | undefined = getTouch(
								args,
								pointer.id
							);

							if (!touch) {
								return;
							}

							pointerMoveHandler(touch, state);
						},
						{ signal }
					);
					window.addEventListener(
						"touchend",
						(args) => {
							const touch: Touch | undefined = getTouch(
								args,
								pointer.id
							);

							if (!touch) {
								return;
							}

							pointerUpHandler(touch, state);
						},
						{ signal }
					);
					break;
				default:
					never(pointer);
			}

			return (): void => {
				cancellation.abort();

				dragStatusRef.current = null;
				setState(null);
			};
		},
		[
			pointerMoveHandler,
			pointerUpHandler,
			isEnabled,
			state
		]
	);

	const excludeFromDraggingCallbackFactory = useCallback(
		(identifier: string): RefCallback<HTMLElement> => {
			return (node: HTMLElement | null): void => {
				if (node) {
					elementsToExcludeIdMapRef.current
						.set(identifier, node);
				} else {
					elementsToExcludeIdMapRef.current
						.delete(identifier);
				}
			};
		},
		[]
	);

	return useMemo(
		(): UseDraggableResult => ({
			ref: setElement,
			excludeController: {
				[draggableExcludeCallbackFactory]: excludeFromDraggingCallbackFactory
			}
		}),
		[excludeFromDraggingCallbackFactory]
	);
}

type DragPointer = {
	pageStartOffset: Coordinate;
	offsetInElement: Coordinate;
} & ({
	kind: "mouse";
} | {
	kind: "touch";
	id: number;
});

type DragScrollContainer = {
	element: HTMLElement;
	startOffset: Coordinate;
};

type DraggableState = {
	pointer: DragPointer;
	scrollContainer: DragScrollContainer | null;
};

type DragStatus = "pending" | "active";

const OVERFLOW_REGEX = /(auto|scroll)/;

function findNearestScrollContainer(
	element: HTMLElement
): HTMLElement | null {
	while (element.parentElement) {
		const parent: HTMLElement = element.parentElement;

		const computedStyles: CSSStyleDeclaration = window.getComputedStyle(parent);

		if (
			OVERFLOW_REGEX.test(
				computedStyles.overflow
				+ computedStyles.overflowX
				+ computedStyles.overflowY
			)
		) {
			return parent;
		}

		element = parent;
	}

	if (document.scrollingElement instanceof HTMLElement) {
		return document.scrollingElement;
	}

	return null;
}

export function useExcludeFromDraggingRef(excludeController: DraggableExcludeController): RefCallback<HTMLElement> {
	const identifierRef: RefObject<string> = useRef<string>(null!);

	if (identifierRef.current === null) {
		identifierRef.current = newGuid();
	}

	const callback: (identifier: string) => RefCallback<HTMLElement> = excludeController[draggableExcludeCallbackFactory];

	return useMemo(
		(): RefCallback<HTMLElement> => callback(identifierRef.current),
		[callback]
	);
}

function hasMovedEnough(movement: Coordinate): boolean {
	const { x, y } = movement;
	return Math.abs(x) >= 5 || Math.abs(y) >= 5;
}

function getTouch(
	args: TouchEvent,
	identifier: number
): Touch | undefined {
	return Array.from(args.changedTouches)
		.find(x => x.identifier === identifier);
}

function getDragArguments(
	args: MouseEvent | Touch,
	state: DraggableState
): DragCallbackArgs {
	const movement: Coordinate = getMovement(
		args,
		state.pointer,
		state.scrollContainer
	);

	return {
		movement,
		clientX: args.clientX - state.pointer
			.offsetInElement
			.x,
		clientY: args.clientY - state.pointer
			.offsetInElement
			.y,
		pointerClientX: args.clientX,
		pointerClientY: args.clientY
	};
}

function getMovement(
	args: MouseEvent | Touch,
	pointer: DragPointer,
	scrollContainer: DragScrollContainer | null
): Coordinate {
	const movement: Coordinate = {
		x: args.pageX - pointer.pageStartOffset
			.x,
		y: args.pageY - pointer.pageStartOffset
			.y
	};

	if (scrollContainer && scrollContainer.element !== document.scrollingElement) {
		const { element, startOffset } = scrollContainer;

		movement.y += element.scrollTop - startOffset.y;
		movement.x += element.scrollLeft - startOffset.x;
	}

	return movement;
}

type EdgeScrollingState = {
	readonly horizontalSpeed: number;
	readonly verticalSpeed: number;
	readonly startArgs?: MouseEvent | Touch;
};

const defaultEdgeScrollingState: EdgeScrollingState = {
	horizontalSpeed: 0,
	verticalSpeed: 0
};

const SCROLL_SENSITIVENESS = 20;
const SCROLL_SPEED = 20;